import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { periodLabel } from "@/lib/pricing";
import { useSubscription } from "@/context/SubscriptionContext";
import { fmtPlainCVE, PaymentFooter } from "@/components/PaymentLogos";
import { downloadReceipt, V4StatusBadge } from "@/components/Vinti4Payments";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, HourglassMedium, ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";
import { BackButton } from "@/components/BackButton";

const COPY = {
  succeeded: [CheckCircle, "text-emerald-600", "Pagamento confirmado", "O seu plano está ativo. Enviámos o Recibo por email."],
  failed: [XCircle, "text-red-600", "Pagamento recusado", "O cartão não foi debitado. Pode tentar novamente."],
  cancelled: [XCircle, "text-slate-500", "Pagamento cancelado", "Cancelou o pagamento na página Vinti4."],
  pending_verification: [HourglassMedium, "text-amber-600", "Pagamento a verificar", "Estamos a confirmar o pagamento com a SISP."],
  pending: [HourglassMedium, "text-amber-600", "Pagamento pendente", "Ainda não recebemos a confirmação da SISP."],
};

export default function PaymentResult() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const { refresh } = useSubscription();
  const [p, setP] = useState(null);
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    if (ref) api.get(`/vinti4/payments/by-ref/${ref}`).then((r) => { setP(r.data); refresh(); }).catch(() => {});
  }, [ref, refresh]);
  const status = p?.status || params.get("status") || "pending";
  const [Icon, color, title, text] = COPY[status] || COPY.pending;
  const check = async () => {
    setChecking(true);
    try { const { data } = await api.post(`/vinti4/payments/${p.id}/check-status`); setP(data.payment); refresh(); } catch { /* shown as unchanged */ }
    setChecking(false);
  };
  return (
    <div className="flex min-h-screen flex-col items-center justify-between bg-slate-50 px-4 py-12" data-testid="payment-result-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
        <div className="-mt-4 mb-2 text-left"><BackButton fallback="/o-meu-plano" className="-ml-4" testid="payment-result-back-top" /></div>
        <Icon size={56} weight="fill" className={`mx-auto ${color}`} />
        <h1 className="mt-4 font-display text-2xl font-extrabold" data-testid="payment-result-title">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{text}</p>
        {params.get("error") && status === "failed" && <p className="mt-2 text-xs text-red-600" data-testid="payment-result-error">{params.get("error")}</p>}
        {p && (
          <div className="mt-6 space-y-1 rounded-xl bg-secondary p-4 text-left text-sm">
            <div className="flex justify-between"><span>Plano {p.plan_name} · {periodLabel(p.months)}</span><V4StatusBadge status={p.status} testid="payment-result-status" /></div>
            <div className="font-display text-xl font-extrabold" data-testid="payment-result-amount">{fmtPlainCVE(p.amount_cve)}</div>
            <div className="text-xs text-muted-foreground">Referência de pagamento: <span className="font-mono" data-testid="payment-result-ref">{p.merchant_ref}</span></div>
          </div>
        )}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {p?.status === "succeeded" && <Button variant="outline" className="rounded-full" onClick={() => downloadReceipt(p)} data-testid="payment-result-receipt"><DownloadSimple size={16} className="mr-1.5" /> Descarregar Recibo</Button>}
          {p && ["pending", "pending_verification"].includes(p.status) && (
            <Button variant="outline" className="rounded-full" disabled={checking} onClick={check} data-testid="payment-result-check"><ArrowsClockwise size={16} className="mr-1.5" /> Verificar estado</Button>
          )}
          <Button className="rounded-full" asChild data-testid="payment-result-back"><Link to="/o-meu-plano">Voltar a O meu plano</Link></Button>
        </div>
      </div>
      <div className="mt-10"><PaymentFooter /></div>
    </div>
  );
}
